/**
 * Enhanced Card component for consistent content containers
 */

import { Box, BoxProps, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import { BaseComponentProps, SpacingSize, CardVariant } from '../../types';

export interface CardProps
  extends BaseComponentProps,
    Omit<BoxProps, 'padding'> {
  /** Card variant */
  variant?: CardVariant;
  /** Internal padding */
  padding?: SpacingSize;
  /** Whether card is clickable */
  clickable?: boolean;
  /** Callback when card is clicked */
  onClick?: () => void;
  /** Whether card is selected/active */
  isSelected?: boolean;
  /** Whether to show loading state */
  loading?: boolean;
}

export const Card: React.FC<CardProps> = ({
  variant = 'elevated',
  padding = 6,
  clickable = false,
  onClick,
  isSelected = false,
  loading = false,
  className,
  testId,
  children,
  ...props
}) => {
  const lightBg = useColorModeValue('white', 'gray.800');
  const outlineBg = useColorModeValue('transparent', 'transparent');
  const filledBg = useColorModeValue('gray.50', 'gray.700');
  const subtleBg = useColorModeValue('gray.25', 'gray.750');

  const lightBorder = useColorModeValue('gray.200', 'gray.600');
  const selectedBorder = useColorModeValue('blue.300', 'blue.400');

  const getVariantStyles = () => {
    const baseStyles = {
      borderRadius: 'lg',
      transition: 'all 0.2s ease-in-out',
      position: 'relative' as const,
      ...(loading && { opacity: 0.6, pointerEvents: 'none' as const }),
    };

    switch (variant) {
      case 'elevated':
        return {
          ...baseStyles,
          bg: lightBg,
          shadow: isSelected ? 'md' : 'sm',
          border: isSelected ? `2px solid` : 'none',
          borderColor: isSelected ? selectedBorder : 'transparent',
          _hover: clickable
            ? { shadow: 'md', transform: 'translateY(-1px)' }
            : {},
        };

      case 'outline':
        return {
          ...baseStyles,
          bg: outlineBg,
          border: '1px solid',
          borderColor: isSelected ? selectedBorder : lightBorder,
          _hover: clickable ? { borderColor: selectedBorder } : {},
        };

      case 'filled':
        return {
          ...baseStyles,
          bg: filledBg,
          border: isSelected ? `2px solid` : 'none',
          borderColor: isSelected ? selectedBorder : 'transparent',
          _hover: clickable ? { bg: lightBg, shadow: 'sm' } : {},
        };

      case 'subtle':
        return {
          ...baseStyles,
          bg: subtleBg,
          border: isSelected ? `2px solid` : 'none',
          borderColor: isSelected ? selectedBorder : 'transparent',
          _hover: clickable ? { bg: filledBg } : {},
        };

      default:
        return baseStyles;
    }
  };

  return (
    <Box
      {...getVariantStyles()}
      p={padding}
      cursor={clickable ? 'pointer' : 'default'}
      onClick={onClick}
      className={className}
      data-testid={testId}
      {...props}
    >
      {children}
    </Box>
  );
};

export interface CardHeaderProps extends BaseComponentProps {
  /** Header title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Action buttons for the header */
  actions?: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  actions,
  className,
  testId,
  children,
}) => {
  const subTitleColor = useColorModeValue('gray.600', 'gray.400');
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      mb={4}
      className={className}
      data-testid={testId}
    >
      <Box flex="1">
        <Box
          fontSize="lg"
          fontWeight="semibold"
          color={useColorModeValue('gray.800', 'gray.100')}
        >
          {title}
        </Box>
        {subtitle && (
          <Box fontSize="sm" color={subTitleColor} mt={1}>
            {subtitle}
          </Box>
        )}
        {children}
      </Box>
      {actions && (
        <Box ml={4} flexShrink={0}>
          {actions}
        </Box>
      )}
    </Box>
  );
};
